import { Injectable } from "@angular/core";
import { AssemblaTicketService } from "../models/integrations/assembla-ticket.service";
import { BitbucketIssueService } from "../models/integrations/bitbucket-issue.service";
import { GithubIssueService } from "../models/integrations/github-issue.service";
import { GitlabIssueService } from "../models/integrations/gitlab-issue.service";
import { JiraIssueService } from "../models/integrations/jira-issue.service";
import { LighthouseTicketService } from "../models/integrations/lighthouse-ticket.service";
import { PivotalStoryService } from "../models/integrations/pivotal-story.service";
import { TrelloCardService } from "../models/integrations/trello-card.service";

@Injectable()
export class IssueIndicatorsService {
  private cachedIssues: Map<string, any>;

  constructor(
    private githubIssueService: GithubIssueService,
    private pivotalStoryService: PivotalStoryService,
    private jiraIssueService: JiraIssueService,
    private lighthouseTicketService: LighthouseTicketService,
    private assemblaTicketService: AssemblaTicketService,
    private bitbucketIssueService: BitbucketIssueService,
    private trelloCardService: TrelloCardService,
    private gitlabIssueService: GitlabIssueService,
  ) {
    this.cachedIssues = new Map();
  }

  public clearCachedIssues() {
    this.cachedIssues.clear();
  }

  public addIndicatorOnGroup(group: any, serviceName: string, issue?: any) {
    for (const issueIndicator of group.issueIndicators) {
      if (issueIndicator.serviceName === serviceName) {
        return;
      }
    }

    const issueKey = this.cachedIssueKey(group, serviceName);

    const cachedIssue = this.cachedIssues.get(issueKey);
    if (cachedIssue) {
      return this.pushNewIndicator(group, serviceName, cachedIssue);
    }

    if (issue) {
      this.cachedIssues.set(issueKey, issue);
      return this.pushNewIndicator(group, serviceName, issue);
    }

    const service = (() => {
      switch (serviceName) {
        case "assembla":
          return this.assemblaTicketService;
        case "bitbucket":
          return this.bitbucketIssueService;
        case "github":
          return this.githubIssueService;
        case "gitlab":
          return this.gitlabIssueService;
        case "jira":
          return this.jiraIssueService;
        case "lighthouse":
          return this.lighthouseTicketService;
        case "pivotal":
          return this.pivotalStoryService;
        case "trello":
          return this.trelloCardService;
      }
    })();

    if (!service) {
      return;
    }

    const notice = { projectId: group.projectId, groupId: group.id };

    return service.get(notice).subscribe((fetchedIssue: any) => {
      if (!fetchedIssue.url) {
        return;
      }
      this.cachedIssues.set(issueKey, fetchedIssue);
      return this.pushNewIndicator(group, serviceName, fetchedIssue);
    });
  }

  public removeIndicatorFromGroup(group: any, serviceName: string) {
    for (const issueIndicator of group.issueIndicators) {
      if (issueIndicator.serviceName === serviceName) {
        this.cachedIssues.delete(this.cachedIssueKey(group, serviceName));
        const index = group.issueIndicators.indexOf(issueIndicator);
        group.issueIndicators.splice(index, 1);
        break;
      }
    }
  }

  private indicatorTooltipText(serviceName: string) {
    const tooltipPrefix = "View ";

    const tooltipText = (() => {
      switch (serviceName) {
        case "assembla":
          return "Assembla ticket";
        case "bitbucket":
          return "Bitbucket issue";
        case "github":
          return "GitHub issue";
        case "gitlab":
          return "GitLab issue";
        case "jira":
          return "JIRA issue";
        case "lighthouse":
          return "Lighthouse ticket";
        case "pivotal":
          return "Pivotal Tracker story";
        case "trello":
          return "Trello card";
      }
    })();

    return tooltipPrefix + tooltipText;
  }

  private pushNewIndicator(group: any, serviceName: string, issue: any) {
    const newIssueIndicator = {
      url: issue.url,
      tooltipText: this.indicatorTooltipText(serviceName),
      serviceName,
    };
    return group.issueIndicators.push(newIssueIndicator);
  }

  private cachedIssueKey(group: any, serviceName: string) {
    return `${group.id}-${serviceName}`;
  }
}
